import React from 'react';
import styled from 'styled-components';
import { Card } from '../../../components/common';
import tenantTheme from '@theme';
import { v2Color } from '@theme/v2';

export const PropertyCard = styled((props) => <Card {...props} />)`
  &.active-card {
    background: linear-gradient(180deg, ${v2Color('rgba(0, 97, 105, 0.36)')} -164.55%, ${v2Color('rgba(0, 97, 105, 0)')} 57.27%);
    border: 1px solid ${tenantTheme['primary-color']};
  }
`;

const Chevron = ({ size = 18, strokeWidth = 2.4, className }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: 'block' }}
  >
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Points along the reading direction, so it flips with the page in Arabic. */
export const DirectionalChevron = styled(Chevron)`
  transform: scaleX(${({ theme }) => (theme.rtl ? -1 : 1)});
`;

export const ChooserHeader = styled.div`
  margin-bottom: ${({ $isMobile }) => ($isMobile ? '20px' : '32px')};
  text-align: ${({ $isMobile }) => ($isMobile ? 'center' : 'start')};

  h1,
  h6 {
    margin: 0 0 6px;
    font-size: ${({ $isMobile }) => ($isMobile ? '16px' : '26px')};
    font-weight: 700;
    line-height: ${({ $isMobile }) => ($isMobile ? '20px' : '1.25')};
    color: ${({ $isMobile }) => ($isMobile ? v2Color('rgb(39, 43, 65)') : 'rgba(0, 0, 0, 0.88)')};
  }
`;

export const ChooserSubtitle = styled.span`
  font-size: ${({ $isMobile }) => ($isMobile ? '12px' : '15px')};
  color: rgba(0, 0, 0, 0.45);
`;

export const ChooserGrid = styled.div`
  display: grid;
  gap: ${({ $isMobile }) => ($isMobile ? '12px' : '14px')};
  align-items: stretch;
  width: 100%;
  grid-template-columns: ${({ $isMobile }) => ($isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))')};
`;

export const OptionCard = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  padding: ${({ $isMobile }) => ($isMobile ? '16px' : '22px 20px')};
  background: #fff;
  border: 1.5px solid #ebecf0;
  border-radius: 10px;
  text-align: start;
  cursor: pointer;
  /* Buttons do not inherit the page font, and the app has no global reset for it. Without this
     the card content falls back to the browser's default, which is obvious in Arabic. */
  font-family: inherit;
  font-size: inherit;
  transition:
    border-color 0.18s ease,
    background 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease;

  /* Hover state is published as custom properties so the footer swap reacts to it without
     selector interpolation across styled components. */
  --option-meta-opacity: 1;
  --option-action-opacity: 0;

  ${({ $isMobile }) =>
    $isMobile
      ? /* Touch has no hover, so the card answers the press itself. The lift, shadow and footer
           swap stay off: the mobile design specifies only the border and background. */
        `
      &:active,
      &:focus-visible {
        border-color: #28b16d;
        background: #F7FDFA;
      }
    `
      : `
      &:hover,
      &:focus-visible {
        border-color: #28b16d;
        background: #F6FCF9;
        box-shadow: rgba(16, 24, 40, 0.1) 0 10px 24px -8px;
        transform: translateY(-2px);
        --option-meta-opacity: 0;
        --option-action-opacity: 1;
      }
    `}
`;

export const OptionCardHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

export const OptionIconBox = styled.span`
  display: grid;
  place-content: center;
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: ${({ $isMobile }) => ($isMobile ? v2Color('#F2FAFA') : 'transparent')};
  margin-bottom: ${({ $isMobile }) => ($isMobile ? '0' : '16px')};
`;

export const OptionBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
`;

export const OptionTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.88);
  line-height: 1.35;
  padding-inline-end: ${({ $isMobile }) => ($isMobile ? '0' : '24px')};
`;

export const OptionDescription = styled.span`
  margin-top: ${({ $isMobile }) => ($isMobile ? '5px' : '6px')};
  font-size: 13.5px;
  color: rgba(0, 0, 0, 0.45);
  line-height: 1.5;
  text-wrap: pretty;
`;

export const OptionChevron = styled.span`
  display: grid;
  place-content: center;
  flex: 0 0 auto;
  margin-top: 12px;
  color: rgba(0, 0, 0, 0.25);
`;

export const OptionFooter = styled.div`
  flex: 0 0 auto;
  margin-top: ${({ $isMobile }) => ($isMobile ? '14px' : 'auto')};
  padding-top: ${({ $isMobile }) => ($isMobile ? '0' : '14px')};
`;

export const OptionFooterRow = styled.div`
  display: grid;
  justify-items: start;
  padding-top: 12px;
  border-top: 1px solid ${({ $isMobile }) => ($isMobile ? '#EFF1F5' : 'rgba(0, 0, 0, 0.06)')};
`;

export const OptionMeta = styled.div`
  grid-area: 1 / 1;
  font-size: 12.5px;
  color: ${({ $isMobile }) => ($isMobile ? 'rgba(0, 0, 0, 0.45)' : 'rgba(0, 0, 0, 0.4)')};
  line-height: 1.5;
  opacity: var(--option-meta-opacity, 1);
  transition: opacity 0.18s ease;
`;

export const OptionAction = styled.div`
  grid-area: 1 / 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: ${v2Color('#28b16d', 'text')};
  opacity: var(--option-action-opacity, 0);
  transition: opacity 0.18s ease;
`;

export const NewRibbon = styled.span`
  position: absolute;
  background: #f5222d;
  color: #fff;
  font-weight: 700;
  white-space: nowrap;

  ${({ $isMobile }) =>
    $isMobile
      ? `
        top: -1px;
        inset-inline-end: 14px;
        padding: 0 8px;
        font-size: 11px;
        letter-spacing: 0.04em;
        line-height: 19px;
        border-radius: 0 0 4px 4px;
      `
      : `
        top: 8px;
        inset-inline-end: -8px;
        padding: 0 8px;
        font-size: 14px;
        font-weight: 400;
        line-height: 22px;
        border-radius: 4px;
        border-end-end-radius: 0;
      `}
`;

/** The folded corner under the desktop ribbon's trailing edge. */
export const NewRibbonFold = styled.span`
  position: absolute;
  top: 100%;
  inset-inline-end: 0;
  width: 8px;
  height: 8px;
  border: 4px solid #f5222d;
  border-inline-end-color: transparent;
  border-block-end-color: transparent;
  transform: scaleY(0.75);
  transform-origin: top;
  filter: brightness(75%);
`;
